import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button } from 'antd';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useNavigate } from 'react-router-dom';
import { route } from '../../../../../constants/routes';
import { Header } from '../../../../../core/components';
import { Interview } from '../models/interview.model';
import { InterviewService } from '../services/interview.service';
import {
  buildCalendarEntries,
  CalendarInterview,
} from '../utils/interview-calendar.utils';
import {
  CalendarView,
  InterviewAgendaView,
  InterviewCalendarToolbar,
  InterviewMonthView,
  LoadState,
} from './InterviewCalendarViews';
import './InterviewAgendaPage.css';

dayjs.extend(utc);

const InterviewAgendaPage: React.FC = () => {
  const navigate = useNavigate();
  const [value, setValue] = useState<Dayjs>(() => dayjs());
  const [items, setItems] = useState<Interview[]>([]);
  const [calendarView, setCalendarView] = useState<CalendarView>('month');
  const [loadState, setLoadState] = useState<LoadState>('loading');
  const [refreshKey, setRefreshKey] = useState(0);

  const visibleMonth = value.format('YYYY-MM');
  const range = useMemo(() => {
    const month = dayjs(`${visibleMonth}-01`);
    return {
      from: month
        .startOf('month')
        .startOf('week')
        .utc()
        .format('YYYY-MM-DDTHH:mm:ss'),
      to: month
        .endOf('month')
        .endOf('week')
        .utc()
        .format('YYYY-MM-DDTHH:mm:ss'),
    };
  }, [visibleMonth]);

  useEffect(() => {
    let cancelled = false;

    const loadAgenda = async () => {
      setLoadState('loading');
      try {
        const data = await InterviewService.agenda(range.from, range.to);
        if (!cancelled) {
          setItems(Array.isArray(data) ? data : []);
          setLoadState('ready');
        }
      } catch {
        if (!cancelled) {
          setLoadState('error');
        }
      }
    };

    void loadAgenda();

    return () => {
      cancelled = true;
    };
  }, [range.from, range.to, refreshKey]);

  const entries = useMemo(() => buildCalendarEntries(items), [items]);

  const byDay = useMemo(() => {
    const map = new Map<string, CalendarInterview[]>();
    for (const entry of entries) {
      const key = dayjs(entry.startsAt).format('YYYY-MM-DD');
      const dayEntries = map.get(key) || [];
      dayEntries.push(entry);
      map.set(key, dayEntries);
    }
    return map;
  }, [entries]);

  const agendaGroups = useMemo(() => {
    const groups = new Map<string, CalendarInterview[]>();
    for (const entry of entries) {
      const key = dayjs(entry.startsAt).format('YYYY-MM-DD');
      const group = groups.get(key) || [];
      group.push(entry);
      groups.set(key, group);
    }
    return Array.from(groups.entries());
  }, [entries]);

  const selectedItems = byDay.get(value.format('YYYY-MM-DD')) || [];
  const confirmedCount = items.reduce(
    (total, interview) => total + (interview.status === 'CONFIRMED' ? 1 : 0),
    0
  );
  const proposedCount = items.reduce(
    (total, interview) => total + (interview.status === 'PROPOSED' ? 1 : 0),
    0
  );

  const openApplication = (jobApplicationId: string) => {
    navigate(route(`/job-applications/${jobApplicationId}`));
  };

  const reviewApplications = () => {
    navigate(route('/job-applications'));
  };

  return (
    <div className="interview-calendar-page">
      <Header
        title="Interview Agenda"
        subtitle="Review upcoming conversations and keep candidate schedules moving."
      />

      <main className="ac-page-content interview-calendar-content">
        <section
          className="interview-calendar-surface"
          aria-labelledby="calendar-period-title"
        >
          <InterviewCalendarToolbar
            value={value}
            itemCount={items.length}
            calendarView={calendarView}
            onViewChange={setCalendarView}
            onToday={() => setValue(dayjs())}
            onChangeMonth={(amount) =>
              setValue((current) => current.add(amount, 'month'))
            }
          />

          {loadState === 'error' ? (
            <Alert
              className="interview-calendar-alert"
              type="error"
              showIcon
              message="We couldn’t load the Interview Agenda"
              description="Check your connection and try again."
              action={
                <Button
                  size="small"
                  onClick={() => setRefreshKey((current) => current + 1)}
                >
                  Try again
                </Button>
              }
            />
          ) : null}

          {calendarView === 'month' ? (
            <InterviewMonthView
              value={value}
              byDay={byDay}
              selectedItems={selectedItems}
              loadState={loadState}
              summary={{
                total: items.length,
                confirmed: confirmedCount,
                proposed: proposedCount,
              }}
              onSelectDate={setValue}
              onOpenApplication={openApplication}
              onReviewApplications={reviewApplications}
            />
          ) : (
            <InterviewAgendaView
              groups={agendaGroups}
              loadState={loadState}
              onOpenApplication={openApplication}
              onReviewApplications={reviewApplications}
            />
          )}
        </section>
      </main>
    </div>
  );
};

export default InterviewAgendaPage;
